import { useMemo } from 'react';
import { useQuery } from 'urql';

import {
  type CustomRoleFilter,
  REFERENCE_LISTS_QUERY,
  type TalentDiscoveryRespUserV3,
  TaxonomyType,
} from '@revelio/data-access';

import { useTaxonomyTree } from '../../../shared/selection-lists/hooks/useTaxonomyTree';
import { normalizeReferenceLists } from '../../../shared/selection-lists/utils/referenceLists';
import { treeToGeoSelectionLists } from '../../../shared/selection-lists/utils/treeToGeoSelectionLists';
import { treeToRoleSelectionLists } from '../../../shared/selection-lists/utils/treeToRoleSelectionLists';
import { normalizeForMatch } from '../utils/normalizeForMatch';
import {
  type GeoSelectionLists,
  useLocationMapping,
} from './useLocationMapping';
import type { RoleSelectionData } from './useRoleMapping';
import { useRoleMapping } from './useRoleMapping';

// ── Shared types (re-exported for consumers) ──

export type GeoType = 'region' | 'country' | 'msa' | 'city';

export interface GeoLevel {
  id: number;
  type: GeoType;
  name: string;
}

export type RoleGranularity =
  | 'role_k15000'
  | 'role_k1500'
  | 'role_k150'
  | 'role_k7';

export interface RoleLevel {
  id: number;
  type: RoleGranularity;
  name: string;
  /** The levelId string for use in CustomRoleFilter entities (e.g. 'role_k150') */
  levelId: string;
}

/** Build a CustomRoleFilter from a RoleLevel (for use in GraphQL filters). */
export const buildCustomRoleFilter = (role: RoleLevel): CustomRoleFilter => ({
  taxonomyId: { default: true },
  entities: [{ id: String(role.id), levelId: role.levelId }],
});

export interface ResolvedProfileIds {
  roleId: number | null;
  roleName: string | null;
  /** All resolved role levels, from most specific to broadest */
  roleLevels: RoleLevel[];
  seniorityId: number | null;
  companyRcid: number | null;
  companyName: string | null;
  /** All resolved geo levels (city, msa, country, region) */
  geoLevels: GeoLevel[];
  /** The currently selected geo level index */
  primaryGeo: GeoLevel | null;
}

// ── Shared utilities (used by sub-hooks) ──

export const parseId = (
  id: string | number | null | undefined
): number | null => {
  if (typeof id === 'number') return id;
  if (typeof id === 'string') {
    const parsed = parseInt(id, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

// ── Internal helpers ──

interface MatchableItem {
  shortName?: string | null;
  longName?: string | null;
  id?: string | number | null;
}

const findBestMatch = <T extends MatchableItem>(
  items: T[],
  searchText: string
): T | null => {
  const normalized = normalizeForMatch(searchText);
  if (!normalized) return null;

  const exact = items.find(
    (item) =>
      normalizeForMatch(item.shortName) === normalized ||
      normalizeForMatch(item.longName) === normalized
  );
  if (exact) return exact;

  const contains = items.find((item) => {
    const short = normalizeForMatch(item.shortName);
    const long = normalizeForMatch(item.longName);
    return (
      (short && (normalized.includes(short) || short.includes(normalized))) ||
      (long && (normalized.includes(long) || long.includes(normalized)))
    );
  });
  if (contains) return contains;

  return null;
};

const EMPTY_RESOLVED: ResolvedProfileIds = {
  roleId: null,
  roleName: null,
  roleLevels: [],
  seniorityId: null,
  companyRcid: null,
  companyName: null,
  geoLevels: [],
  primaryGeo: null,
};

/** Ordered from most specific to least specific */
const GEO_LEVELS_ORDERED: GeoType[] = ['city', 'msa', 'country', 'region'];

const GEO_TAXONOMY_LEVELS = [1, 2, 3, 4] as const;

// role_k7 … role_k15000 (the 7 role tree levels useRoleMapping traverses).
const ROLE_TAXONOMY_LEVELS = [1, 2, 3, 4, 5, 6, 7] as const;

// ── Main hook ──

export const useResolveProfileIds = (
  user: TalentDiscoveryRespUserV3 | null
): { resolvedIds: ResolvedProfileIds; isResolving: boolean } => {
  const [{ data: referenceListsData, fetching: referenceListsFetching }] =
    useQuery({ query: REFERENCE_LISTS_QUERY });
  const referenceLists = useMemo(
    () => normalizeReferenceLists(referenceListsData),
    [referenceListsData]
  );

  const {
    treeData: geoTreeData,
    levels: geoLevels,
    fetching: geoFetching,
  } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Geography,
    taxonomyId: 1,
    levels: [...GEO_TAXONOMY_LEVELS],
  });

  const geoLists = useMemo((): GeoSelectionLists | null => {
    // Geo is a hard dependency: keep resolving (null) until the tree arrives,
    // matching the pre-migration selection-list behavior.
    if (geoFetching || geoTreeData.length === 0) return null;
    return treeToGeoSelectionLists(
      geoTreeData,
      geoLevels
    ) as unknown as GeoSelectionLists;
  }, [geoTreeData, geoLevels, geoFetching]);

  const {
    treeData: roleTreeData,
    levels: roleLevels,
    fetching: rolesFetching,
  } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Role,
    taxonomyId: 0,
    levels: [...ROLE_TAXONOMY_LEVELS],
    pause: !user,
  });

  // Role level lists for the role mapping hook, sourced from the role
  // TaxonomyTree (parent-linked, carrying rawTitles — ROLE trees are fetched
  // with includeTitles, which useRoleMapping's fallback matching relies on).
  const roleSelectionData = useMemo((): RoleSelectionData | null => {
    if (rolesFetching || roleTreeData.length === 0) return null;
    const lists = treeToRoleSelectionLists(roleTreeData, roleLevels);
    if (!lists.role_k150) return null;
    return {
      role_k7: lists.role_k7 ?? [],
      role_k50: lists.role_k50 ?? [],
      role_k150: lists.role_k150 ?? [],
      role_k500: lists.role_k500 ?? [],
      role_k1500: lists.role_k1500 ?? [],
      role_k5000: lists.role_k5000 ?? [],
      role_k15000: lists.role_k15000 ?? [],
    } as unknown as RoleSelectionData;
  }, [rolesFetching, roleTreeData, roleLevels]);

  // Resolve role via the Role Mapping API (async)
  const { role: mappedRole, isLoading: isRoleMappingLoading } = useRoleMapping(
    user?.current_title,
    roleSelectionData
  );

  // Extract user location string for the location mapper
  const userLocation = useMemo(() => {
    if (!user) return null;
    const primaryWork = user.work_history?.[0];
    return user.user_location || primaryWork?.location || null;
  }, [user]);

  // Resolve geography via the Location Mapping API (async)
  const { location: mappedLocation, isLoading: isLocationMappingLoading } =
    useLocationMapping(userLocation, geoLists);

  const resolvedIds = useMemo((): ResolvedProfileIds => {
    if (!user) return EMPTY_RESOLVED;

    const primaryWork = user.work_history?.[0];
    const result: ResolvedProfileIds = {
      ...EMPTY_RESOLVED,
      roleLevels: [],
      geoLevels: [],
    };

    // Company
    if (primaryWork?.RCID && primaryWork.RCID > 0) {
      result.companyRcid = primaryWork.RCID;
      result.companyName = user.current_company || null;
    }

    // Role: use the async role mapping result
    result.roleId = mappedRole.roleId;
    result.roleName = mappedRole.roleName;
    result.roleLevels = mappedRole.roleLevels;

    // Seniority: infer from title keywords
    if (referenceLists?.seniority) {
      const seniorityList = referenceLists.seniority;
      const title = (user.current_title || '').toLowerCase();
      const seniorityKeywords: Record<string, string[]> = {
        'C-Suite': ['ceo', 'cfo', 'cto', 'coo', 'cmo', 'chief', 'c-suite'],
        Director: ['director', 'head of'],
        'Vice President': ['vice president', 'vp'],
        Manager: ['manager', 'lead', 'principal', 'staff'],
        Associate: ['associate', 'senior', 'sr.', 'sr '],
        Junior: ['junior', 'jr.', 'jr '],
        'Entry Level': ['intern', 'entry', 'trainee', 'graduate'],
      };

      for (const [seniorityName, keywords] of Object.entries(
        seniorityKeywords
      )) {
        if (keywords.some((kw) => title.includes(kw))) {
          const match = seniorityList.find(
            (s) =>
              normalizeForMatch(s.shortName) ===
                normalizeForMatch(seniorityName) ||
              normalizeForMatch(s.longName) === normalizeForMatch(seniorityName)
          );
          if (match) {
            const id = parseId(match.id);
            if (id !== null) {
              result.seniorityId = id;
              break;
            }
          }
        }
      }
    }

    // Geography: prefer Location Mapping API results (exact IDs) over fuzzy matching
    if (mappedLocation.geoLevels.length > 0) {
      result.geoLevels = mappedLocation.geoLevels;
    } else if (geoLists) {
      // Fallback: fuzzy matching against selection lists
      const location = user.user_location || primaryWork?.location || '';
      if (location) {
        const locationParts = [location];
        if (location.includes(',')) {
          locationParts.push(...location.split(',').map((p) => p.trim()));
        }

        for (const geoType of GEO_LEVELS_ORDERED) {
          const list = geoLists[geoType] ?? [];
          for (const part of locationParts) {
            const match = findBestMatch([...list], part);
            if (match) {
              const id = parseId(match.id);
              if (id !== null) {
                result.geoLevels.push({
                  id,
                  type: geoType,
                  name: match.longName || match.shortName || geoType,
                });
                break;
              }
            }
          }
        }
      }
    }

    if (result.geoLevels.length > 0) {
      result.primaryGeo = result.geoLevels[0] ?? null;
    }

    return result;
  }, [user, mappedRole, mappedLocation, referenceLists, geoLists]);

  const isResolving =
    rolesFetching ||
    isRoleMappingLoading ||
    isLocationMappingLoading ||
    referenceListsFetching ||
    geoFetching;

  return { resolvedIds, isResolving };
};
